import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, ShieldCheck, Mail, Phone, Building2, Pencil } from "lucide-react";
import PhoneInput from "../../components/PhoneInput";
import { checkPhone, fullPhone } from "../../lib/validation";

export default function AdminManagers() {
    const [managers, setManagers] = useState([]);
    const [centres, setCentres] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", centre_ids: [] });
    const [submitting, setSubmitting] = useState(false);

    const fetchAll = async () => {
        const [m, c] = await Promise.all([
            api.get("/admin/managers"),
            api.get("/centres"),
        ]);
        setManagers(m.data);
        setCentres(c.data);
    };
    useEffect(() => { fetchAll(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: "", email: "", phone: "", password: "", centre_ids: [] });
        setOpen(true);
    };

    const openEdit = (m) => {
        setEditing(m);
        setForm({
            name: m.name || "",
            email: m.email || "",
            phone: (m.phone || "").replace(/^\+91/, ""),
            password: "",
            centre_ids: m.managed_centres || [],
        });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (form.phone && !checkPhone(form.phone).ok) { toast.error("Invalid phone"); return; }
        if (form.password && form.password.length > 0 && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        setSubmitting(true);
        try {
            if (editing) {
                const payload = {
                    name: form.name,
                    email: form.email.toLowerCase(),
                    centre_ids: form.centre_ids,
                };
                if (form.phone) payload.phone = fullPhone(form.phone);
                if (form.password && form.password.length >= 6) payload.password = form.password;
                await api.put(`/admin/managers/${editing.id}`, payload);
                toast.success(form.password ? "Manager updated (password reset)" : "Manager updated");
            } else {
                await api.post("/admin/managers", {
                    name: form.name, email: form.email.toLowerCase(),
                    phone: fullPhone(form.phone), password: form.password,
                    centre_ids: form.centre_ids,
                });
                toast.success("Manager created");
            }
            setOpen(false);
            setEditing(null);
            setForm({ name: "", email: "", phone: "", password: "", centre_ids: [] });
            fetchAll();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed");
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id) => {
        if (!confirm("Remove this manager?")) return;
        await api.delete(`/admin/managers/${id}`);
        toast.success("Removed");
        fetchAll();
    };

    const updateCentres = async (m, ids) => {
        await api.put(`/admin/managers/${m.id}`, { centre_ids: ids });
        toast.success("Updated");
        fetchAll();
    };

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Managers</h1>
                        <p className="text-slate-400 text-sm mt-1">Centre-restricted staff with admin powers for assigned venues only.</p>
                    </div>
                    <button onClick={openCreate} data-testid="new-manager-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                        <Plus className="w-4 h-4" /> New Manager
                    </button>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {managers.length === 0 && <div className="text-slate-500 col-span-2 text-center py-8">No managers yet.</div>}
                    {managers.map(m => (
                        <div key={m.id} className="bg-slate-900 border border-slate-800 rounded-lg p-5 relative" data-testid={`manager-${m.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">
                                        <ShieldCheck className="w-3 h-3" /> Manager
                                    </div>
                                    <h3 className="font-display text-lg font-bold uppercase text-white mt-2">{m.name}</h3>
                                    <div className="text-xs text-slate-400 mt-1 space-y-1">
                                        <div className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</div>
                                        {m.phone && <div className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</div>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => openEdit(m)} data-testid={`edit-manager-${m.id}`}
                                        className="text-slate-400 hover:text-sky-400" title="Edit">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => remove(m.id)} data-testid={`del-manager-${m.id}`}
                                        className="text-slate-500 hover:text-red-400" title="Remove">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3">
                                <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500 mb-2 inline-flex items-center gap-1">
                                    <Building2 className="w-3 h-3" /> Assigned centres
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {centres.map(c => {
                                        const sel = (m.managed_centres || []).includes(c.id);
                                        return (
                                            <button key={c.id}
                                                onClick={() => updateCentres(m, sel ? m.managed_centres.filter(x => x !== c.id) : [...(m.managed_centres || []), c.id])}
                                                className={`px-2 h-7 text-[10px] font-bold uppercase tracking-widest rounded-full border ${
                                                    sel ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-950 text-slate-400 border-slate-700 hover:border-slate-500"
                                                }`}>{c.name.replace("Drive or Kick ", "")}</button>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {open && (
                    <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                        <form onSubmit={submit} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3" data-testid="manager-form">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">{editing ? "Edit Manager" : "New Manager"}</h3>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Name</label>
                                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required minLength={2}
                                    data-testid="mgr-name" className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Email</label>
                                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                                    data-testid="mgr-email" className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            </div>
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Mobile</label>
                                <PhoneInput value={form.phone} onChange={v => setForm({ ...form, phone: v })} testid="mgr-phone" />
                            </div>
                            {!editing ? (
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Password</label>
                                    <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={6}
                                        data-testid="mgr-password" className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                                </div>
                            ) : (
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Reset Password (optional)</label>
                                    <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} minLength={6}
                                        placeholder="Leave blank to keep current password"
                                        data-testid="mgr-edit-password" className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-amber-400" />
                                    <div className="text-[10px] text-slate-500 mt-1">Min 6 chars. Leave blank to keep current password.</div>
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Assign centres</label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {centres.map(c => {
                                        const sel = form.centre_ids.includes(c.id);
                                        return (
                                            <button key={c.id} type="button"
                                                onClick={() => setForm({ ...form, centre_ids: sel ? form.centre_ids.filter(x => x !== c.id) : [...form.centre_ids, c.id] })}
                                                className={`px-3 h-8 text-[10px] font-bold uppercase tracking-widest rounded-full border ${
                                                    sel ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-950 text-slate-400 border-slate-700"
                                                }`}>{c.name}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={() => { setOpen(false); setEditing(null); }} className="flex-1 h-11 border border-slate-700 rounded text-slate-300">Cancel</button>
                                <button type="submit" disabled={submitting} data-testid="save-manager-btn" className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded">{submitting ? "Saving..." : (editing ? "Update" : "Create")}</button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
